import React, { useState, useEffect } from "react";
import { Button, Card, message, Spin } from "antd";
import api from "../utils/axios";
import { useDispatch } from "react-redux";
import { setLoading } from "../redux/loadingSlice";
import useLang from "../hooks/useLang";
import { CONFIG } from "../config";

interface OrderPriceChangeRequestDto {
	id: number;
	driverId: number;
	orderId: number;
	approvedOrDenied?: boolean;
	newOrderPrice: number;
	comment: string;
}

interface Props {
	orderId: string | null;
	onButtonsPress: () => void;
}

const OrderPriceChangeRequest: React.FC<Props> = ({
	orderId,
	onButtonsPress,
}) => {
	const dispatch = useDispatch();
	const [request, setRequest] = useState<OrderPriceChangeRequestDto | null>(
		null
	);
	const { strings } = useLang("orderPriceChangeRequest");
	useEffect(() => {
		const fetchRequest = async () => {
			if (!orderId) {
				return;
			}
			dispatch(setLoading(true));
			try {
				const response = await api.get(
					"OrderPriceChangeRequest/GetByOrderId",
					{
						params: { orderId },
					}
				);
				setRequest(response.data);
			} catch (error) {
				message.error(strings?.fetchRequestError);
			} finally {
				dispatch(setLoading(false));
			}
		};

		fetchRequest();
	}, [orderId]);

	const handleApproval = async (approved: boolean) => {
		if (!request) return;

		const updatedRequest: OrderPriceChangeRequestDto = {
			...request,
			approvedOrDenied: approved,
		};

		try {
			dispatch(setLoading(true));
			await api.post(
				"/OrderPriceChangeRequest/ApprovedOrDenied",
				updatedRequest
			);
			message.success(
				`Request ${
					approved
						? strings?.requestApprovedSuccess
						: strings?.requestDeniedSuccess
				}`
			);
			onButtonsPress();
			setRequest(null);
		} catch (error) {
			message.error(strings?.updateRequestStatusError);
		} finally {
			dispatch(setLoading(false));
		}
	};

	if (!request || !orderId) {
		return <></>;
	}

	return (
		<Card title={`${strings?.cardTitlePrefix} ${orderId}`}>
			<p>
				<strong>{strings?.newOrderPriceLabel}</strong>{" "}
				{CONFIG?.CURRENCY}
				{request.newOrderPrice.toFixed(2)}
			</p>
			<p>
				<strong>{strings?.commentLabel}</strong> {request.comment}
			</p>
			<Button type="default" onClick={() => handleApproval(true)}>
				{strings?.confirmButton}
			</Button>
			<Button
				type="default"
				onClick={() => handleApproval(false)}
				style={{ marginLeft: 8 }}
			>
				{strings?.refuseButton}
			</Button>
		</Card>
	);
};

export default OrderPriceChangeRequest;
